"use client";

/**
 * AlertTopBar — minimal, editorial header strip for /alerts/{id}.
 *
 * Layout:
 *   ┌────────────────────────────────────────────────────────────────────────┐
 *   │ Alert name  ● Live  (i)    [⏱ Every 15m…] · [✉ syed@…]   [▶ Run now][⏸][•]│
 *   │ Last ran 3m ago                                                         │
 *   └────────────────────────────────────────────────────────────────────────┘
 *
 * Key rules (from product review):
 *   - Run Now icon = Radar (taste pick — NOT Zap/vibe)
 *   - Schedule chip + Email chip sit side-by-side with a dot-separator between
 *     them. Both truncate with "…" and open their edit modal on click.
 *   - (i) info icon next to status pill. Hover → scrollable popover with the
 *     full `explanation_md` rendered as markdown (written by Gemini during
 *     upload_alert and stored on the alert doc).
 *   - NO "Build Your Workflow" button.
 *   - Everything else (Share, Edit name/desc, Change email/schedule, Delete)
 *     lives in the •••  menu.
 */
import { createContext, useContext, useEffect, useState } from "react";

/** Lets every topbar subcomponent read the alert/signal view mode without
 *  threading the prop through 8 helper signatures. */
const ViewModeCtx = createContext<string | undefined>(undefined);
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import {
  BookOpen,
  Clock,
  Radar,
  Play,
  Pause,
  Pencil,
  Mail,
  Trash2,
  Share2,
  Loader2,
  CheckCircle2,
  Check,
  MoreHorizontal,
  Info,
  History,
  Globe,
  GitFork,
  Lock,
  Sparkles,
  Zap,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { apiClient, isPaidRequiredError, type AlertDoc } from "@/lib/api-client";
import { RenameAlertDialog } from "./edit-modals/RenameAlertDialog";
import { EditEmailDialog } from "./edit-modals/EditEmailDialog";
import { EditScheduleDialog } from "./edit-modals/EditScheduleDialog";
import { DeleteAlertDialog } from "./edit-modals/DeleteAlertDialog";
import { AlertTopBarPublic } from "./AlertTopBarPublic";
import { AlertLogsModal } from "./AlertLogsModal";
import { useAlertPublicView } from "./AlertPaywallContext";
import { telemetry } from "@/lib/telemetry";
import { getAutomationGuideByAlertId } from "@/lib/automation-guides";
import { useSidebar } from "@/components/ui/sidebar";
import { SidebarLeftIcon } from "@/components/icons";
import { useAuth } from "@/contexts/AuthContext";
import { useSearchParams } from "next/navigation";
import { AlertViewSwitcher, parseAlertViewTab } from "./AlertViewSwitcher";
import { SubscribeConfirmModal } from "./SubscribeConfirmModal";
import { ConfirmActionModal } from "./ConfirmActionModal";
import { DashboardDownloadMenu } from "./DashboardDownloadMenu";

const getFocusRing = (viewMode?: string) =>
  `focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background ${viewMode === "signal" ? "focus-visible:ring-emerald-400/60" : "focus-visible:ring-sky-400/60"}`;

interface AlertTopBarProps {
  alert: AlertDoc;
  onMutated: () => void;
  onDeleted: () => void;
  /** When true, the right-hand cluster switches to `AlertTopBarPublic`
   *  (Subscribe + LIVE/DELAYED toggle) and the center chips go away.
   *  The left side (title, status, info popover) stays the same. */
  isReadonly?: boolean;
  /** Subscriber's email for the "Subscribed ✓" dropdown. Passed from
   *  page.tsx → Chat → here. Subscription boolean lives in context,
   *  not here, so the toggle + button stay in sync everywhere. */
  subscribedEmail?: string | null;
  /** Owner-only subscriber count. Undefined for non-owners. Shown in
   *  the (i) info popover as "N subscribers". */
  subscriberCount?: number;
  viewMode?: string;
}

export function AlertTopBar({
  alert,
  onMutated,
  onDeleted,
  isReadonly = false,
  subscribedEmail = null,
  subscriberCount,
  viewMode,
}: AlertTopBarProps) {
  const { user } = useAuth();
  const { open, openMobile, isMobile, toggleSidebar } = useSidebar();
  const sidebarOpen = isMobile ? openMobile : open;
  const [renameOpen, setRenameOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false);
  const [subscribeModalOpen, setSubscribeModalOpen] = useState(false);
  // Which confirmation modal is open (null = none). Run now / Use template /
  // Pause / Resume all funnel through the one ConfirmActionModal shell.
  const [confirm, setConfirm] = useState<null | "run" | "fork" | "pause">(null);

  const searchParams = useSearchParams();
  const tab = parseAlertViewTab(searchParams?.get("tab") ?? null);

  // Logs button is ALWAYS visible (owner + non-owner). Clicking it when
  // you're not allowed (free tier / signed-out non-owner) triggers the
  // paywall instead of opening the modal. This makes the feature
  // discoverable — previously we hid the button when `!has_snapshots`
  // or when the caller wasn't paid, so paid users coming to an empty
  // alert saw nothing at all. The modal itself has an "empty runs"
  // state so there's no risk of an awkward blank modal.
  const publicView = useAlertPublicView();
  const { canGoLive } = publicView;
  const showLogsButton = true; // always; gate click handler instead

  const handleLogsClick = () => {
    // Owner always opens. Non-owners need paid tier — otherwise route
    // through the paywall. Free-tier users never see past runs.
    if (!isReadonly || canGoLive) {
      setLogsOpen(true);
    } else {
      publicView.openPaywall("live");
    }
  };

  // Subline under the title. For owners: literal last-run time. For
  // non-owners: "Showing: X ago" tied to the CURRENT view so the label
  // stays honest about what's on screen. Previously we always showed
  // "Last ran 18s" even when the dashboard shown was the 5-day-old
  // first snapshot, which was confusing.
  const subline = (() => {
    if (!isReadonly) {
      return `Last ran ${formatLastRun(alert.last_run_at)}`;
    }
    if (publicView.view === "live") {
      return `Showing: ${formatLastRun(alert.last_run_at)}`;
    }
    if (publicView.delayedAgeLabel) {
      return `Showing: ${publicView.delayedAgeLabel} ago (delayed)`;
    }
    return `Last ran ${formatLastRun(alert.last_run_at)}`;
  })();

  const [pauseBusy, setPauseBusy] = useState(false);
  const [runBusy, setRunBusy] = useState(false);
  const [runOk, setRunOk] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [visibilityBusy, setVisibilityBusy] = useState(false);

  const isPaused = alert.status === "paused";
  // Recoverable non-active states. test_failed is usually a first-run timeout
  // artifact; Resume re-arms the schedule (backend allows it), so the owner is
  // never stuck with only Delete.
  const needsResume =
    isPaused || alert.status === "test_failed" || alert.status === "errored";
  const isPublic = Boolean(alert.is_public);
  const automationGuide = getAutomationGuideByAlertId(alert._id);

  useEffect(() => {
    const openEditDetails = () => {
      if (isReadonly) return;
      setRenameOpen(true);
    };
    window.addEventListener("xynth-alert-open-edit-details", openEditDetails);
    return () => {
      window.removeEventListener("xynth-alert-open-edit-details", openEditDetails);
    };
  }, [isReadonly]);

  const handleTogglePause = async () => {
    if (!canGoLive) {
      publicView.openPaywall("manage");
      return;
    }
    const action = needsResume ? "resume" : "pause";
    console.info(`[AlertTopBar] ${action} clicked`, { alertId: alert._id });
    setPauseBusy(true);
    try {
      const res = needsResume
        ? await apiClient.resumeAlert(alert._id)
        : await apiClient.pauseAlert(alert._id);
      console.info(`[AlertTopBar] ${action} ok`, { alertId: alert._id, res });
      onMutated();
    } catch (err) {
      console.error(`[AlertTopBar] ${action} failed`, { alertId: alert._id, err });
      if (isPaidRequiredError(err)) publicView.openPaywall("manage");
    } finally {
      setPauseBusy(false);
    }
  };

  const handleRunNow = async () => {
    if (!canGoLive) {
      publicView.openPaywall("manage");
      return;
    }
    console.info("[AlertTopBar] run-now clicked", { alertId: alert._id });
    setRunBusy(true);
    setRunOk(false);
    try {
      const res = await apiClient.runAlertNow(alert._id);
      console.info("[AlertTopBar] run-now response", { alertId: alert._id, res });
      if (res?.status === "success" || res?.status === "queued") setRunOk(true);
      onMutated();
    } catch (err) {
      console.error("[AlertTopBar] run-now failed", { alertId: alert._id, err });
      if (isPaidRequiredError(err)) publicView.openPaywall("manage");
    } finally {
      setRunBusy(false);
      window.setTimeout(() => setRunOk(false), 2500);
    }
  };

  // Trigger functions: open the confirm modal (or route to paywall) instead of
  // firing the action immediately. The actual work runs on modal confirm.
  const requestRunNow = () => {
    if (!canGoLive) return publicView.openPaywall("manage");
    setConfirm("run");
  };
  const requestTogglePause = () => {
    if (!canGoLive) return publicView.openPaywall("manage");
    setConfirm("pause");
  };
  const requestFork = () => setConfirm("fork");

  const handleShare = async () => {
    console.info("[AlertTopBar] share clicked", {
      alertId: alert._id,
      url: window.location.href,
    });
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShareCopied(true);
      window.setTimeout(() => setShareCopied(false), 1800);
    } catch (err) {
      console.error("[AlertTopBar] clipboard write failed", { err });
    }
  };

  const openMainChatWithQuery = (query: string) => {
    if (typeof window === "undefined") return;
    const url = `${window.location.origin}/?q=${encodeURIComponent(query)}&model_tier=max`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleForkAlert = () => {
    telemetry.emit("alert.fork_click", {
      session_id: alert.linked_session_id || alert._id,
      alert_id: alert._id,
      alert_name: alert.name,
      source: "alert_topbar",
      readonly: isReadonly,
    });
    openMainChatWithQuery(buildForkAlertQuery(alert));
  };

  const handleCreateOwnAlert = () => {
    telemetry.emit("alert.create_click", {
      session_id: alert.linked_session_id || alert._id,
      alert_id: alert._id,
      alert_name: alert.name,
      source: "alert_topbar",
      readonly: isReadonly,
    });
    openMainChatWithQuery(buildCreateAlertQuery(alert));
  };

  // Owner-only toggle. Flipping is_public:
  //   - true  → other users' agents can read this alert via find_automations/get_automation
  //             and visitors get a read-only sidebar chat about it on /alerts/{id}.
  //   - false → reverts to private; agents stop returning it.
  // No confirmation modal — the action is reversible and low-risk
  // (doesn't share email, doesn't expose user_id thanks to _serialize_alert).
  const handleToggleVisibility = async () => {
    if (!canGoLive) {
      publicView.openPaywall("manage");
      return;
    }
    const next = !isPublic;
    console.info("[AlertTopBar] visibility toggle clicked", {
      alertId: alert._id,
      from: isPublic,
      to: next,
    });
    setVisibilityBusy(true);
    try {
      const res = await apiClient.updateAlertVisibility(alert._id, next);
      console.info("[AlertTopBar] visibility ok", { alertId: alert._id, res });
      onMutated();
    } catch (err) {
      console.error("[AlertTopBar] visibility failed", { alertId: alert._id, err });
      if (isPaidRequiredError(err)) publicView.openPaywall("manage");
    } finally {
      setVisibilityBusy(false);
    }
  };

  return (
    <ViewModeCtx.Provider value={viewMode}>
      {/* On mobile the action cluster (Run now / Pause / More) gets cramped
          against the title when we only rely on `gap-4`. `justify-between`
          pushes LEFT (title) to the edge and RIGHT (buttons) to the other
          edge, then the center chips (hidden on mobile via `md:flex`) just
          disappear. Tighter padding + gap on mobile so the row breathes
          without sacrificing the comfortable desktop spacing.

          `data-tour="alert-topbar"` is the anchor for the onboarding tour's
          first scene 2 step ("This is your alert"). The full row is the
          target so the spotlight wraps the whole header. */}
      {/* `overflow-x-auto` stays at every breakpoint: between ~768–1100px the
          left (title + status + info), fixed-floor center chips (min-w 220),
          and right action cluster (shrink-0) collectively exceed the viewport,
          and the previous `md:overflow-visible` made `justify-between` bleed
          items across each other. Horizontal scroll is safe because both
          `AlertInfoPopover` (Radix Popover) and the ⋯ menu (Radix Dropdown)
          portal to <body>, so they're not clipped by the scroll context. */}
      <div
        data-tour="alert-topbar"
        className="flex items-center justify-between lg:grid lg:grid-cols-[1fr_auto_1fr] gap-1.5 px-2 md:gap-4 md:px-5 h-12 md:h-14 border-b border-border/60 bg-background/85 backdrop-blur-md overflow-x-auto overflow-y-hidden overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {/* ── LEFT: name + status + last-ran subline ─────── */}
        <div className="flex items-center gap-1.5 md:gap-3 min-w-0 shrink-0 lg:shrink lg:justify-self-start">
          {user && (
            <button
              type="button"
              data-tour="sidebar-opener"
              data-testid="sidebar-opener-button"
              aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"}
              aria-pressed={sidebarOpen}
              onClick={toggleSidebar}
              className={[
                "inline-flex h-7 w-7 md:h-9 md:w-9 shrink-0 items-center justify-center rounded-full",
                "border border-white/10 bg-background/70 text-foreground shadow-sm backdrop-blur",
                "transition-colors hover:bg-white/[0.08]",
                getFocusRing(viewMode),
              ].join(" ")}
            >
              <SidebarLeftIcon size={16} />
            </button>
          )}
          <div className="min-w-0 flex flex-col">
            <div className="flex items-center gap-1.5 md:gap-2.5 min-w-0">
              <h1 className="text-[12px] md:text-[16px] font-semibold text-foreground leading-tight tracking-tight truncate max-w-[72px] md:max-w-[320px]">
                {alert.name}
              </h1>
              <StatusDot status={alert.status} />
            </div>
            <p
              title={
                alert.last_run_at
                  ? `Last ran ${formatLastRunAbsolute(alert.last_run_at)}`
                  : undefined
              }
              className="text-[11px] text-muted-foreground font-medium tabular-nums leading-tight mt-0.5 hidden md:block"
            >
              {subline}
            </p>
          </div>
        </div>

        {/* ── CENTER: view switcher — pinned dead-center via the grid's
            auto column, so it never drifts regardless of title / action
            cluster widths. ── */}
        <div className="justify-self-center">
          <AlertViewSwitcher active={tab} viewMode={viewMode} />
        </div>

        {/* ── RIGHT: dynamic context cluster ──────────────────────────────── */}
        <div data-tour="alert-controls" className="flex items-center gap-1.5 whitespace-nowrap justify-self-end">
          {tab === "dashboard" && (
            <>
              {isReadonly ? (
                <>
                  <span className="hidden md:inline-flex">
                    <GhostIconButton
                      icon={<History className="w-3.5 h-3.5" />}
                      label="View past runs"
                      onClick={handleLogsClick}
                    />
                  </span>
                  <DashboardDownloadMenu alertId={alert._id} isOwner={false} />
                  <AlertTopBarPublic alert={alert} subscribedEmail={subscribedEmail} />
                </>
              ) : (
                <>
                  <span data-tour="alert-action-run-now" className="inline-flex">
                    <SolidButton
                      icon={
                        runOk ? (
                          <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2.4} />
                        ) : (
                          <Zap className="w-3.5 h-3.5" strokeWidth={2.4} fill="currentColor" />
                        )
                      }
                      busy={runBusy}
                      onClick={requestRunNow}
                      label={runOk ? "Fired!" : "Run now"}
                      hideLabelOnMobile
                    />
                  </span>
                  <span data-tour="alert-action-pause" className="hidden md:inline-flex">
                    <GhostIconButton
                      icon={
                        needsResume ? (
                          <Play className="w-3.5 h-3.5" />
                        ) : (
                          <Pause className="w-3.5 h-3.5" />
                        )
                      }
                      label={needsResume ? "Resume" : "Pause"}
                      onClick={requestTogglePause}
                      busy={pauseBusy}
                    />
                  </span>
                  {showLogsButton && (
                    <span data-tour="alert-history" className="hidden md:inline-flex">
                      <GhostIconButton
                        icon={<History className="w-3.5 h-3.5" />}
                        label="View past runs"
                        onClick={handleLogsClick}
                      />
                    </span>
                  )}
                  <DashboardDownloadMenu alertId={alert._id} isOwner={!isReadonly} />
                  <span className="hidden md:inline-flex">
                    <GhostIconButton
                      icon={shareCopied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Share2 className="w-3.5 h-3.5" />}
                      label={shareCopied ? "Link copied" : "Share"}
                      onClick={handleShare}
                    />
                  </span>
                  {/* Dashboard mobile dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="md:hidden inline-flex items-center justify-center w-7 h-7 rounded-md border border-border/60 text-foreground/80"
                      >
                        <MoreHorizontal className="w-4 h-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-[200px]">
                      <DropdownMenuItem onClick={requestTogglePause}>
                        {needsResume ? <Play className="w-3.5 h-3.5 mr-2" /> : <Pause className="w-3.5 h-3.5 mr-2" />}
                        {needsResume ? "Resume" : "Pause"}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleLogsClick}>
                        <History className="w-3.5 h-3.5 mr-2" /> View past runs
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleShare}>
                        {shareCopied ? <Check className="w-3.5 h-3.5 mr-2 text-emerald-500" /> : <Share2 className="w-3.5 h-3.5 mr-2" />}
                        {shareCopied ? "Link copied" : "Share"}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              )}
            </>
          )}

          {tab === "description" && (
            <>
              {isReadonly ? (
                <>
                  <SecondaryButton
                    icon={<History className="w-3.5 h-3.5" />}
                    label="Logs"
                    hideLabelOnMobile
                    onClick={handleLogsClick}
                  />
                  <SolidButton
                    icon={<Mail className="w-3.5 h-3.5" />}
                    label="Subscribe"
                    onClick={() => {
                      if (!canGoLive) {
                        publicView.openPaywall("live");
                      } else {
                        setSubscribeModalOpen(true);
                      }
                    }}
                  />
                </>
              ) : (
                <>
                  <SecondaryButton
                    icon={shareCopied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Share2 className="w-3.5 h-3.5" />}
                    label={shareCopied ? "Copied" : "Share"}
                    hideLabelOnMobile
                    onClick={handleShare}
                  />
                  <SecondaryButton
                    icon={<Pencil className="w-3.5 h-3.5" />}
                    label="Edit details"
                    hideLabelOnMobile
                    onClick={() => setRenameOpen(true)}
                  />
                </>
              )}
            </>
          )}

          {tab === "canvas" && (
            <>
              {isReadonly ? (
                <>
                  <span className="hidden md:inline-flex">
                    <GhostIconButton
                      icon={<History className="w-3.5 h-3.5" />}
                      label="View past runs"
                      onClick={handleLogsClick}
                    />
                  </span>
                  <SolidButton
                    icon={<Mail className="w-3.5 h-3.5" />}
                    label="Subscribe"
                    onClick={() => {
                      if (!canGoLive) {
                        publicView.openPaywall("live");
                      } else {
                        setSubscribeModalOpen(true);
                      }
                    }}
                  />
                  {/* Signals are curated by the Xynth team — visitors can only
                      subscribe, never fork or spin up their own. Regular alerts
                      keep "Use template" + "Create own" for virality. */}
                  {viewMode !== "signal" && (
                    <>
                      <SecondaryButton
                        icon={<GitFork className="w-3.5 h-3.5" />}
                        label="Use template"
                        hideLabelOnMobile
                        onClick={requestFork}
                      />
                      <SolidButton
                        icon={<Sparkles className="w-3.5 h-3.5" />}
                        label="Create own"
                        hideLabelOnMobile
                        onClick={handleCreateOwnAlert}
                      />
                    </>
                  )}
                </>
              ) : (
                <>
                  <span data-tour="alert-action-run-now" className="inline-flex">
                    <SolidButton
                      icon={
                        runOk ? (
                          <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2.4} />
                        ) : (
                          <Zap className="w-3.5 h-3.5" strokeWidth={2.4} fill="currentColor" />
                        )
                      }
                      busy={runBusy}
                      onClick={requestRunNow}
                      label={runOk ? "Fired!" : "Run now"}
                      hideLabelOnMobile
                    />
                  </span>
                  <SecondaryButton
                    icon={<GitFork className="w-3.5 h-3.5" />}
                    label="Use template"
                    hideLabelOnMobile
                    onClick={requestFork}
                  />
                  <GhostIconButton
                    icon={shareCopied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Share2 className="w-3.5 h-3.5" />}
                    label={shareCopied ? "Link copied" : "Share"}
                    onClick={handleShare}
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>

      <RenameAlertDialog
        open={renameOpen}
        onOpenChange={setRenameOpen}
        alert={alert}
        canMutate={canGoLive}
        onSaved={onMutated}
        onPaidRequired={() => publicView.openPaywall("manage")}
        viewMode={viewMode}
      />
      <EditEmailDialog
        open={emailOpen}
        onOpenChange={setEmailOpen}
        alert={alert}
        onSaved={onMutated}
        onPaidRequired={() => publicView.openPaywall("manage")}
        viewMode={viewMode}
      />
      <EditScheduleDialog
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        alert={alert}
        canMutate={canGoLive}
        onSaved={onMutated}
        onPaidRequired={() => publicView.openPaywall("manage")}
        viewMode={viewMode}
      />
      <DeleteAlertDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        alert={alert}
        onDeleted={onDeleted}
        onPaidRequired={() => publicView.openPaywall("manage")}
        viewMode={viewMode}
      />
      <AlertLogsModal
        open={logsOpen}
        onClose={() => setLogsOpen(false)}
        alertId={alert._id}
        alertName={alert.name}
      />
      <SubscribeConfirmModal
        open={subscribeModalOpen}
        onOpenChange={setSubscribeModalOpen}
        onConfirm={async () => {
          await publicView.subscribe();
          setSubscribeModalOpen(false);
        }}
        viewMode={viewMode}
        busy={publicView.subscribeBusy}
        email={user?.email || null}
      />

      {/* Run now */}
      <ConfirmActionModal
        open={confirm === "run"}
        onOpenChange={(o) => !o && setConfirm(null)}
        busy={runBusy}
        Icon={Zap}
        accent="icy"
        title="Run this workflow now?"
        subtitle={alert.name}
        description="This will start a new run right away and update your dashboard. Your regular schedule will not change."
        confirmLabel="Run now"
        onConfirm={async () => {
          await handleRunNow();
          setConfirm(null);
        }}
      />

      {/* Use this template (fork) */}
      <ConfirmActionModal
        open={confirm === "fork"}
        onOpenChange={(o) => !o && setConfirm(null)}
        Icon={GitFork}
        accent="slate"
        title="Use this as a template?"
        subtitle={alert.name}
        description="This opens a new chat where you can make your own version of this workflow. You can change the tickers, schedule, or rules step by step with the AI. The original workflow on this page stays exactly as it is."
        confirmLabel="Start my copy"
        onConfirm={() => {
          handleForkAlert();
          setConfirm(null);
        }}
      />

      {/* Pause / Resume */}
      <ConfirmActionModal
        open={confirm === "pause"}
        onOpenChange={(o) => !o && setConfirm(null)}
        busy={pauseBusy}
        Icon={needsResume ? Play : Pause}
        accent={needsResume ? "emerald" : "amber"}
        title={needsResume ? "Resume this workflow?" : "Pause this workflow?"}
        subtitle={`Runs ${humanSchedule(alert.schedule_description, alert.schedule)}`}
        description={
          needsResume
            ? `This will turn ${alert.name} back on. It will start checking the market and sending you emails again on the same schedule.`
            : "If you pause, this workflow will stop checking the market. You will not get any more emails from it until you turn it back on."
        }
        confirmLabel={needsResume ? "Resume" : "Pause"}
        onConfirm={async () => {
          await handleTogglePause();
          setConfirm(null);
        }}
      />
    </ViewModeCtx.Provider>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Subcomponents
// ─────────────────────────────────────────────────────────────────────────────

function StatusDot({ status }: { status: string }) {
  const viewMode = useContext(ViewModeCtx);
  // test_failed / errored MUST NOT fall through to green "Live" — that told
  // owners a dead alert was running (2026-08-02 incident). Red dot + honest
  // label; the Resume button next to it is the recovery path.
  const spec =
    status === "paused"
      ? { color: "bg-amber-400", label: "Paused" }
      : status === "test_failed" || status === "errored"
        ? { color: "bg-red-400", label: "Not running" }
        : status === "testing"
          ? { color: viewMode === "signal" ? "bg-emerald-400" : "bg-sky-400", label: "Testing" }
          : { color: "bg-emerald-400", label: "Live" };
  const showPing = status !== "paused" && status !== "test_failed" && status !== "errored";
  return (
    <AnimatePresence mode="wait">
      <motion.span
        key={status}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.18 }}
        className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground"
      >
        <span className={`relative flex w-1.5 h-1.5 rounded-full ${spec.color}`}>
          {showPing && (
            <span
              className={`absolute inset-0 rounded-full ${spec.color} animate-ping opacity-75`}
            />
          )}
        </span>
        <span className="hidden md:inline">{spec.label}</span>
      </motion.span>
    </AnimatePresence>
  );
}

/**
 * AlertInfoPopover — (i) icon that opens a narrow, editorial popover with the
 * Gemini-generated `explanation_md`. Rendered via Radix Portal so it escapes
 * the topbar flex row. Width is locked via inline style to defeat any class
 * conflicts from the shared `PopoverContent` base styles.
 *
 * Also normalizes legacy em-dash output (old alerts stored in Mongo) into the
 * new colon style at render time.
 */
function AlertInfoPopover({
  alert,
  subscriberCount,
}: {
  alert: AlertDoc;
  subscriberCount?: number;
}) {
  const viewMode = useContext(ViewModeCtx);
  const rawMd = (alert.explanation_md || "").trim();
  const md = normalizeExplanationMd(rawMd);
  const fallback = (alert.description || "").trim();
  const hasContent = md.length > 0 || fallback.length > 0;

  // The popover was originally gated on having explanation content, but
  // the subscriber_count line is owner-only info that's also valuable
  // even for alerts with no explanation yet — so we show the popover
  // whenever the owner has ANY subscribers too.
  const hasSubscriberStat = typeof subscriberCount === "number";
  if (!hasContent && !hasSubscriberStat) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="What this alert does"
          title="What this alert does"
          className={[
            "inline-flex items-center justify-center w-4 h-4 rounded-full",
            viewMode === "signal" ? "text-muted-foreground/70 hover:text-emerald-400 hover:bg-emerald-400/10" : "text-muted-foreground/70 hover:text-sky-400 hover:bg-sky-400/10",
            "transition-colors",
            getFocusRing(viewMode),
          ].join(" ")}
        >
          <Info className="w-3 h-3" strokeWidth={2.4} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={10}
        style={{ width: 380, maxWidth: "calc(100vw - 1.5rem)" }}
        className="max-h-[min(70vh,440px)] overflow-y-auto p-0 border-border/70 shadow-xl rounded-lg"
      >
        <div className="px-4 py-3 border-b border-border/60 flex items-center gap-2 bg-muted/30">
          <Info className={`w-3.5 h-3.5 shrink-0 ${viewMode === "signal" ? "text-emerald-400" : "text-sky-400"}`} strokeWidth={2.2} />
          <span className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            About this alert
          </span>
        </div>
        {hasSubscriberStat && (
          <div className={`px-4 py-2.5 border-b border-border/60 flex items-center gap-2 ${viewMode === "signal" ? "bg-emerald-500/5" : "bg-sky-500/5"}`}>
            <Mail className={`w-3.5 h-3.5 shrink-0 ${viewMode === "signal" ? "text-emerald-300" : "text-sky-300"}`} strokeWidth={2.2} />
            <span className="text-[11.5px] text-foreground/90 font-medium tabular-nums">
              {subscriberCount}
            </span>
            <span className="text-[11.5px] text-muted-foreground">
              {subscriberCount === 1 ? "subscriber receiving emails" : "subscribers receiving emails"}
            </span>
          </div>
        )}
        {hasContent && (
          <div className="px-4 py-4">
            <div
              className={[
                "prose prose-sm prose-invert max-w-none",
                "text-[12.5px] leading-[1.6] text-foreground/85",
                "[&_p]:my-0 [&_p]:text-foreground/85",
                "[&_p+p]:mt-2.5",
                "[&_ul]:mt-3 [&_ul]:mb-0 [&_ol]:mt-3 [&_ol]:mb-0 [&_ul]:pl-4 [&_ol]:pl-4",
                "[&_li]:my-1 [&_li]:leading-[1.55] [&_li]:marker:text-muted-foreground/60",
                "[&_strong]:text-foreground [&_strong]:font-semibold",
                "[&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded",
                viewMode === "signal" ? "[&_code]:text-[11.5px] [&_code]:text-emerald-300 [&_code]:font-normal" : "[&_code]:text-[11.5px] [&_code]:text-sky-300 [&_code]:font-normal",
              ].join(" ")}
            >
              {md ? (
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{md}</ReactMarkdown>
              ) : (
                <p>{fallback}</p>
              )}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

/**
 * Turn legacy "**Label** — sentence" em-dash output (stored on older alerts
 * before the backend prompt change) into clean "**Label:** sentence" lines
 * with newlines between them, so markdown renders each label on its own line.
 */
function normalizeExplanationMd(md: string): string {
  if (!md) return md;
  // 1. Replace `**Label** — ` / `**Label** – ` / `**Label** - ` with `**Label:** `.
  let out = md.replace(/(\*\*[^*]+\*\*)\s*[—–-]\s+/g, "$1 ");
  // 2. Ensure `**What it watches**` and `**When it emails you**` start on
  //    their own line even if the model glued them into one paragraph.
  out = out.replace(/\s+(\*\*When it emails you\*\*)/g, "\n\n$1");
  out = out.replace(/\s+(\*\*Logic:?\*\*)/g, "\n\n$1");
  // 3. Add a colon after the lead label if it's missing (e.g. "**What it watches** scans for...").
  out = out.replace(/(\*\*What it watches\*\*)(?!:)/g, "$1:");
  out = out.replace(/(\*\*When it emails you\*\*)(?!:)/g, "$1:");
  return out;
}

function GuideChip({ slug, title }: { slug: string; title: string }) {
  const viewMode = useContext(ViewModeCtx);
  return (
    <Link
      href={`/blog/${slug}`}
      title={`Read full guide: ${title}`}
      className={[
        "inline-flex items-center gap-1.5 h-8 px-3 rounded-md",
        viewMode === "signal" ? "border border-emerald-400/30 bg-emerald-400/10 text-emerald-100" : "border border-sky-400/30 bg-sky-400/10 text-sky-100",
        viewMode === "signal" ? "text-[12px] font-semibold hover:bg-emerald-400/15 hover:border-emerald-300/45 transition-colors" : "text-[12px] font-semibold hover:bg-sky-400/15 hover:border-sky-300/45 transition-colors",
        "min-w-0 max-w-full overflow-hidden",
        getFocusRing(viewMode),
      ].join(" ")}
    >
      <BookOpen className="w-3.5 h-3.5 shrink-0" />
      <span className="min-w-0 truncate max-w-[120px]">Read full guide</span>
    </Link>
  );
}

/**
 * EditableChip — dashed-border, clickable chip used for schedule + email.
 * Truncates with "…" at `maxWidth`. Hover reveals the pencil.
 */
function EditableChip({
  icon,
  value,
  title,
  onClick,
  maxWidth,
  disabled,
}: {
  icon: React.ReactNode;
  value: string;
  title: string;
  onClick?: () => void;
  maxWidth: number;
  disabled?: boolean;
}) {
  const viewMode = useContext(ViewModeCtx);
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={`${title}\n\n${value}`}
      className={[
        "inline-flex items-center gap-1.5 h-8 px-3 rounded-md",
        disabled ? "border border-border/40 bg-card/20" : "border border-dashed border-border/70 bg-card/40",
        "text-[12px] font-medium text-foreground/85",
        disabled
          ? "cursor-default"
          : "hover:text-foreground hover:bg-card hover:border-border transition-colors cursor-pointer",
        "min-w-0 max-w-full overflow-hidden",
        getFocusRing(viewMode),
      ].join(" ")}
    >
      {icon}
      <span
        className="min-w-0 truncate"
        style={{ maxWidth: `${maxWidth}px` }}
      >
        {value}
      </span>
      {!disabled && (
        <Pencil
          className="w-3 h-3 text-muted-foreground/60 shrink-0"
          strokeWidth={2}
        />
      )}
    </button>
  );
}

function GhostIconButton({
  icon,
  label,
  onClick,
  busy,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  busy?: boolean;
}) {
  const viewMode = useContext(ViewModeCtx);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      title={label}
      aria-label={label}
      className={[
        "inline-flex items-center justify-center w-7 h-7 rounded-md shrink-0",
        "border border-border/60 bg-transparent",
        "text-foreground/80 hover:text-foreground hover:bg-muted",
        "transition-colors disabled:opacity-60 disabled:cursor-not-allowed",
        getFocusRing(viewMode),
      ].join(" ")}
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : icon}
    </button>
  );
}

function SolidButton({
  icon,
  label,
  onClick,
  busy,
  hideLabelOnMobile,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  busy?: boolean;
  hideLabelOnMobile?: boolean;
}) {
  const viewMode = useContext(ViewModeCtx);
  return (
    <motion.button
      type="button"
      whileTap={{ scale: 0.97 }}
      onClick={onClick}
      disabled={busy}
      title={label}
      className={[
        "inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md shrink-0 whitespace-nowrap",
        viewMode === "signal" ? "bg-emerald-500 hover:bg-emerald-400 text-white" : "bg-sky-500 hover:bg-sky-400 text-white",
        "text-[12px] font-semibold transition-colors",
        "shadow-[0_1px_0_0_rgba(255,255,255,0.15)_inset,0_1px_2px_0_rgba(0,0,0,0.3)]",
        "disabled:opacity-70 disabled:cursor-not-allowed",
        getFocusRing(viewMode),
      ].join(" ")}
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : icon}
      <span className={hideLabelOnMobile ? "hidden md:inline" : undefined}>{label}</span>
    </motion.button>
  );
}

function SecondaryButton({
  icon,
  label,
  onClick,
  hideLabelOnMobile,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  hideLabelOnMobile?: boolean;
}) {
  const viewMode = useContext(ViewModeCtx);
  return (
    <motion.button
      type="button"
      whileTap={{ scale: 0.97 }}
      onClick={onClick}
      title={label}
      className={[
        "inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md shrink-0 whitespace-nowrap",
        "border border-border/70 bg-transparent",
        "text-[12px] font-semibold text-foreground/85",
        "hover:text-foreground hover:bg-muted transition-colors",
        getFocusRing(viewMode),
      ].join(" ")}
    >
      {icon}
      <span className={hideLabelOnMobile ? "hidden md:inline" : undefined}>{label}</span>
    </motion.button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Mobile control clusters (md:hidden) — primary CTA + ⋯ overflow
// ─────────────────────────────────────────────────────────────────────────────

const MOBILE_MENU_ITEM = "text-[13px]";

function MobileOwnerControls({
  isPaused,
  isPublic,
  runBusy,
  runOk,
  pauseBusy,
  visibilityBusy,
  shareCopied,
  canGoLive,
  onRunNow,
  onTogglePause,
  onLogsClick,
  onShare,
  onRename,
  onEmail,
  onSchedule,
  onToggleVisibility,
  onDelete,
}: {
  isPaused: boolean;
  isPublic: boolean;
  runBusy: boolean;
  runOk: boolean;
  pauseBusy: boolean;
  visibilityBusy: boolean;
  shareCopied: boolean;
  canGoLive: boolean;
  onRunNow: () => void;
  onTogglePause: () => void;
  onLogsClick: () => void;
  onShare: () => void;
  onRename: () => void;
  onEmail: () => void;
  onSchedule: () => void;
  onToggleVisibility: () => void;
  onDelete: () => void;
}) {
  const viewMode = useContext(ViewModeCtx);
  return (
    <div className="flex md:hidden items-center gap-1.5 shrink-0 ml-auto">
      {/* Primary: Run now (icon-only on mobile) */}
      <motion.button
        type="button"
        whileTap={{ scale: 0.97 }}
        onClick={onRunNow}
        disabled={runBusy}
        title={runOk ? "Fired!" : "Run now"}
        aria-label="Run now"
        className={[
          "inline-flex items-center justify-center w-9 h-9 rounded-md shrink-0",
          viewMode === "signal" ? "bg-emerald-500 hover:bg-emerald-400 text-white transition-colors" : "bg-sky-500 hover:bg-sky-400 text-white transition-colors",
          "shadow-[0_1px_0_0_rgba(255,255,255,0.15)_inset,0_1px_2px_0_rgba(0,0,0,0.3)]",
          "disabled:opacity-70 disabled:cursor-not-allowed",
          getFocusRing(viewMode),
        ].join(" ")}
      >
        {runBusy ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : runOk ? (
          <CheckCircle2 className="w-4 h-4" strokeWidth={2.4} />
        ) : (
          <Radar className="w-4 h-4" strokeWidth={2.2} />
        )}
      </motion.button>

      {/* Overflow: everything else */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            title="More options"
            aria-label="More options"
            className={[
              "inline-flex items-center justify-center w-9 h-9 rounded-md shrink-0",
              "border border-border/60 bg-transparent text-foreground/80",
              "hover:text-foreground hover:bg-muted transition-colors",
              getFocusRing(viewMode),
            ].join(" ")}
          >
            <MoreHorizontal className="w-4 h-4" strokeWidth={2.2} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" sideOffset={6} className="w-[220px]">
          <DropdownMenuItem className={MOBILE_MENU_ITEM} onClick={onTogglePause} disabled={pauseBusy}>
            {pauseBusy ? (
              <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
            ) : isPaused ? (
              <Play className="w-3.5 h-3.5 mr-2" />
            ) : (
              <Pause className="w-3.5 h-3.5 mr-2" />
            )}
            {isPaused ? "Resume alert" : "Pause alert"}
          </DropdownMenuItem>
          <DropdownMenuItem className={MOBILE_MENU_ITEM} onClick={onLogsClick}>
            <History className="w-3.5 h-3.5 mr-2" />
            View past runs
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem className={MOBILE_MENU_ITEM} onClick={onSchedule}>
            <Clock className="w-3.5 h-3.5 mr-2" />
            Change schedule
          </DropdownMenuItem>
          <DropdownMenuItem className={MOBILE_MENU_ITEM} onClick={onEmail}>
            <Mail className="w-3.5 h-3.5 mr-2" />
            Change email
          </DropdownMenuItem>
          <DropdownMenuItem className={MOBILE_MENU_ITEM} onClick={onRename}>
            <Pencil className="w-3.5 h-3.5 mr-2" />
            Edit name & description
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem className={MOBILE_MENU_ITEM} onClick={onShare}>
            {shareCopied ? (
              <Check className="w-3.5 h-3.5 mr-2 text-emerald-500" />
            ) : (
              <Share2 className="w-3.5 h-3.5 mr-2" />
            )}
            {shareCopied ? "Link copied" : "Copy share link"}
          </DropdownMenuItem>
          <DropdownMenuItem
            className={MOBILE_MENU_ITEM}
            onClick={onToggleVisibility}
            disabled={visibilityBusy}
            onSelect={(e) => {
              if (visibilityBusy) e.preventDefault();
            }}
          >
            {visibilityBusy ? (
              <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
            ) : isPublic ? (
              <Lock className="w-3.5 h-3.5 mr-2" />
            ) : (
              <Globe className="w-3.5 h-3.5 mr-2" />
            )}
            {isPublic ? "Make private" : "Make public"}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className={`${MOBILE_MENU_ITEM} text-destructive focus:text-destructive focus:bg-destructive/10`}
            onClick={onDelete}
          >
            <Trash2 className="w-3.5 h-3.5 mr-2" />
            Delete alert
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function MobileVisitorControls({
  alert,
  publicView,
  onForkAlert,
  onCreateOwnAlert,
  onLogsClick,
}: {
  alert: AlertDoc;
  publicView: ReturnType<typeof useAlertPublicView>;
  onForkAlert: () => void;
  onCreateOwnAlert: () => void;
  onLogsClick: () => void;
}) {
  const viewMode = useContext(ViewModeCtx);
  // Treat failed/errored alerts like paused for visitors: not running, so no
  // subscribing and no live view.
  const isPaused =
    alert.status === "paused" ||
    alert.status === "test_failed" ||
    alert.status === "errored";
  const { view, canGoLive, setView, isSubscribed, subscribeBusy, subscribe, unsubscribe } =
    publicView;

  return (
    <div className="flex md:hidden items-center gap-1.5 shrink-0 ml-auto">
      {/* Primary: Subscribe / Subscribed */}
      <motion.button
        type="button"
        whileTap={{ scale: 0.97 }}
        onClick={isSubscribed ? unsubscribe : subscribe}
        disabled={subscribeBusy || (isPaused && !isSubscribed)}
        title={
          isSubscribed
            ? "You're subscribed — tap to unsubscribe"
            : isPaused
              ? "Subscribing is disabled while paused"
              : "Subscribe to get an email when this fires"
        }
        aria-label={isSubscribed ? "Unsubscribe" : "Subscribe"}
        className={[
          "inline-flex items-center gap-1.5 h-9 px-3 rounded-md shrink-0 whitespace-nowrap",
          isSubscribed
            ? "bg-emerald-500/15 border border-emerald-400/40 text-emerald-200"
            : viewMode === "signal" ? "bg-emerald-500 hover:bg-emerald-400 text-white shadow-[0_1px_0_0_rgba(255,255,255,0.15)_inset,0_1px_2px_0_rgba(0,0,0,0.3)]" : "bg-sky-500 hover:bg-sky-400 text-white shadow-[0_1px_0_0_rgba(255,255,255,0.15)_inset,0_1px_2px_0_rgba(0,0,0,0.3)]",
          "text-[12px] font-semibold transition-colors",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          getFocusRing(viewMode),
        ].join(" ")}
      >
        {subscribeBusy ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : isSubscribed ? (
          <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2.4} />
        ) : (
          <Mail className="w-3.5 h-3.5" strokeWidth={2.2} />
        )}
        {isSubscribed ? "Subscribed" : "Subscribe"}
      </motion.button>

      {/* Overflow: view switch + template/create/history */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            title="More options"
            aria-label="More options"
            className={[
              "inline-flex items-center justify-center w-9 h-9 rounded-md shrink-0",
              "border border-border/60 bg-transparent text-foreground/80",
              "hover:text-foreground hover:bg-muted transition-colors",
              getFocusRing(viewMode),
            ].join(" ")}
          >
            <MoreHorizontal className="w-4 h-4" strokeWidth={2.2} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" sideOffset={6} className="w-[230px]">
          {/* View switch — only when not paused (paused has no live view) */}
          {!isPaused && (
            <>
              <DropdownMenuItem
                className={MOBILE_MENU_ITEM}
                onClick={() => setView("live")}
              >
                {canGoLive ? (
                  <Radar className="w-3.5 h-3.5 mr-2" />
                ) : (
                  <Lock className="w-3.5 h-3.5 mr-2" />
                )}
                <span className="flex-1">Show live</span>
                {view === "live" && <Check className={`w-3.5 h-3.5 ml-2 ${viewMode === "signal" ? "text-emerald-400" : "text-sky-400"}`} />}
              </DropdownMenuItem>
              <DropdownMenuItem
                className={MOBILE_MENU_ITEM}
                onClick={() => setView("delayed")}
              >
                <Clock className="w-3.5 h-3.5 mr-2" />
                <span className="flex-1">Show delayed</span>
                {view === "delayed" && <Check className={`w-3.5 h-3.5 ml-2 ${viewMode === "signal" ? "text-emerald-400" : "text-sky-400"}`} />}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem className={MOBILE_MENU_ITEM} onClick={onForkAlert}>
            <GitFork className="w-3.5 h-3.5 mr-2" />
            Use this template
          </DropdownMenuItem>
          <DropdownMenuItem className={MOBILE_MENU_ITEM} onClick={onCreateOwnAlert}>
            <Sparkles className="w-3.5 h-3.5 mr-2" />
            Create new alert
          </DropdownMenuItem>
          <DropdownMenuItem className={MOBILE_MENU_ITEM} onClick={onLogsClick}>
            <History className="w-3.5 h-3.5 mr-2" />
            View past runs
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Formatters
// ─────────────────────────────────────────────────────────────────────────────

function buildForkAlertQuery(alert: AlertDoc): string {
  return [
    `I want to fork alert ${alert._id}, "${alert.name}".`,
    "Please help me create my own editable version of this alert.",
    "First explain the original logic in simple terms, then ask what I want to change before building it.",
    alert.description ? `Original description: ${alert.description}.` : "",
    alert.schedule_description || alert.schedule
      ? `Original schedule: ${alert.schedule_description || alert.schedule}.`
      : "",
  ]
    .filter(Boolean)
    .join(" ");
}

function buildCreateAlertQuery(alert: AlertDoc): string {
  return [
    "Help me create a new market email alert from scratch.",
    `I was looking at "${alert.name}" and want to build my own alert idea.`,
    "Ask me what ticker, condition, schedule, and email I want before you build it.",
  ].join(" ");
}

function formatLastRun(last_run_at?: string | null): string {
  if (!last_run_at) return "never";
  const t = new Date(last_run_at).getTime();
  if (Number.isNaN(t)) return "—";
  const diffSec = Math.max(1, Math.floor((Date.now() - t) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  // Past 24h, a coarse "Nd ago" reads as "this skipped a day" on a
  // once-a-day signal. Show the exact local date + time instead so the
  // last run is never ambiguous (e.g. "Jun 13 at 2:15 PM").
  return formatLastRunAbsolute(last_run_at);
}

/** Full, unambiguous local timestamp — used for the >24h label and the
 *  hover tooltip on the subline so the precise run time is always one
 *  glance away regardless of how stale it is. */
function formatLastRunAbsolute(last_run_at?: string | null): string {
  if (!last_run_at) return "never";
  const d = new Date(last_run_at);
  if (Number.isNaN(d.getTime())) return "—";
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
    hour: "numeric",
    minute: "2-digit",
  });
}

function humanSchedule(description?: string, cron?: string): string {
  if (description && description.trim()) return description.trim();
  return cron || "no schedule";
}
